import React from "react";
import {Col} from "react-bootstrap";
import PropTypes from "prop-types";
import {GSPSMediaSelector} from "Components/Common/GSPSMediaSelector";
import {GSPSIcon} from "Icons/GSPSIcon";
import {insertNotification} from "Utils/NotificationsUtils";
import {StyledMediaContainer, StyledMediaInput} from "Pages/Audits/Media/AuditMedia.styles";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {useAppleDevice} from "Components/Map/SVPGoogleMapUtils";
import {SVPUpgradeSubscriptionIcon} from "../../../Components/ToolTip/SVPUpgradeSubscriptionIcon";
import {SVPLabel} from "../../../Components/Labels/SVPLabel";
import {DEFAULT_MAX_VIDEO_DURATION} from "../../../Models/Subscriber/SubscriberConstants";

export const initMediaSelector = (elementType, elementId, mediaId, mediaValue) => {
    return {elementType: elementType, elementId: elementId, mediaId: mediaId, mediaValue: mediaValue};
};

export const AuditMedia = ({
    isSmallSizeNoNotes,
    rowMedia,
    updateElementMedia,
    elementType,
    mediaWidth,
    mediaHeight,
    deleteMedia,
    maxVideoDuration,
    uploadCountLimit = 100,
    mediaType = "image",
    isViewOnly = true,
    isFullViewer = true,
}) => {
    const [newNotes, setNewNotes] = React.useState(
        rowMedia.map((media, index) => ({index, notes: media?.notes})),
    );
    const [videoMediaValue, setVideoMediaValue] = React.useState();
    const isDesktop = !useAppleDevice();

    const sliderSettings = {
        dots: true, // Add dots for navigation
        infinite: false, // No infinite looping
        speed: 500,
        slidesToShow: 3, // Show up to 3 items
        slidesToScroll: 1,
        arrows: true, // Show navigation arrows
        dotsClass: "slick-dots custom-dots",
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2, // Show 2 items on tablets
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1, // Show 1 item on mobile
                    slidesToScroll: 1,
                },
            },
        ],
    };

    const mediaIdFromIndex = (issueIndex, mediaId) => {
        return `${mediaType}_${elementType}_${issueIndex}_${mediaId}`;
    };

    const handleChangeImage = (event, index) => {
        event.preventDefault();
        const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes
        const file = event.target.files[0];

        // Check for .mov file extension only on desktop
        if (isDesktop && file.name?.toLowerCase().endsWith(".mov")) {
            insertNotification("Error", "MOV files are not supported.", "error");
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            insertNotification("Error", "File size exceeds 200MB limit", "error");
            return;
        }

        try {
            onMediaChange(elementType, index, index, event.target.files);
        } catch (e) {
            insertNotification("Error", "Couldn't upload File", "error");
        }
    };

    function getMediaTypesFileFormats(mediaType) {
        if (mediaType === "image") {
            return "image/*,.heic";
        } else if (mediaType === "video") {
            return "video/*";
        }
        console.warn(`Unknown Media type ${mediaType} received in MediaSelector component`);
        return "*";
    }


    const onMediaChange = (elementType, index, mediaId, mediaValue, notes = "") => {
        if (mediaType === "video") {
            setVideoMediaValue(mediaValue);
            if (mediaValue === undefined || mediaValue.length === 0) {
                mediaValue = videoMediaValue;
            }
            const video = document.createElement("video");
            video.preload = "metadata";
            const file = mediaValue[0];
            const reader = new FileReader();

            reader.onload = () => {
                video.src = reader.result;

                video.onloadedmetadata = () => {
                    if (video.duration > maxVideoDuration + 2) {
                        insertNotification(
                            "Error",
                            `Video duration exceeds the ${maxVideoDuration}-second limit 
                            (Duration: ${Math.ceil(video.duration)} seconds)`,
                            "error",
                        );
                        return;
                    }
                    updateElementMedia(index, mediaValue, elementType, notes);
                };
            };
            reader.onerror = () => {
                insertNotification("Error", "Failed to load video file", "error");
            };
            reader.readAsDataURL(file);
        } else {
            updateElementMedia(index, mediaValue, elementType);
        }
    };

    return (
        <>
            {!isViewOnly &&
                <>
                    {rowMedia.length < uploadCountLimit ?
                        <StyledMediaInput>
                            <label htmlFor={mediaType + "Upload"}>
                                <GSPSIcon
                                    name={"PlusCircleFill"}
                                    role="button"
                                    color={getComputedStyle(document.body).getPropertyValue("--bs-primary")}
                                />
                            </label>
                            <input
                                style={{visibility: "hidden", width: "1px"}}
                                accept={getMediaTypesFileFormats(mediaType)}
                                id={mediaType + "Upload"}
                                type={"file"}
                                onChange={(e) => handleChangeImage(e, rowMedia.length)}
                                name={mediaType + "Upload"}
                            />
                        </StyledMediaInput> :
                        <SVPUpgradeSubscriptionIcon
                            message={`Upgrade to upload more ${mediaType}s`}
                        />
                    }
                </>
            }

            <Col>
                <StyledMediaContainer>
                    <div style={{width: "100%", paddingLeft: "20px", paddingRight: "20px"}}>
                        <Slider {...sliderSettings}>
                            {rowMedia?.length > 0 && rowMedia.map((media, index) =>(
                                <div className="d-inline position-relative" key={`${elementType}_rowmedia_${index}`}>
                                    <div style={{
                                        height: `${isFullViewer ? "400px" : "100px"}`,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginBottom: isFullViewer ? "0px" : "30px",
                                    }}>
                                        <GSPSMediaSelector
                                            mediaObject={media}
                                            isFullViewer={isFullViewer}
                                            isSmallSizeNoNotes={isSmallSizeNoNotes}
                                            key={mediaIdFromIndex(media.elementId, media.mediaId)}
                                            controlSize={media.mediaId}
                                            imageContainerSizePx={mediaWidth ? mediaWidth : 300}
                                            imageContainerHeightSizePx={mediaHeight ? mediaHeight : 200}
                                            currentImageSrc={
                                                media[`${mediaType}_value`] ? media[`${mediaType}_value`] :
                                                    media.mediaValue ? media.mediaValue :
                                                        undefined
                                            }
                                            notes={media?.notes || ""}
                                            isViewOnly={isViewOnly}
                                            setValue={(id, value) => {
                                                onMediaChange(elementType, id, media.mediaId, value);
                                            }}
                                            onNotesSaved={(isNewNote, value) => {
                                                isNewNote ?
                                                    setNewNotes([...newNotes, {index, notes: value}]) :
                                                    setNewNotes(
                                                        newNotes.map((item) =>
                                                            item.index === index ? {...item, notes: value} : item),
                                                    );

                                                if (media.mediaValue === undefined) {
                                                    if (isNewNote) {
                                                        onMediaChange(
                                                            elementType,
                                                            index,
                                                            media.mediaId,
                                                            undefined,
                                                            value,
                                                        );
                                                    }
                                                } else {
                                                    setNewNotes([...newNotes, {index, notes: value}]);
                                                    updateElementMedia(index, [media.mediaValue], elementType, value);
                                                }
                                            }}
                                            mediaType={mediaType}
                                            media={media}
                                            elementType={elementType}
                                        />
                                        {!isViewOnly &&
                                            <div
                                                className={isFullViewer ? "mt-1" : "my-4"}
                                                style={{fontSize: isFullViewer ? "12px" : "8px"}}
                                            >
                                                Delete Media
                                                <GSPSIcon
                                                    name="TrashFill"
                                                    color="red"
                                                    size={isFullViewer ? "12" : "8"}
                                                    role="button"
                                                    className="text-bg-red mx-2"
                                                    style={{
                                                        position: "relative",
                                                    }}
                                                    onClick={() => {
                                                        deleteMedia(media, index, mediaType);
                                                    }}>
                                                </GSPSIcon>
                                            </div>
                                        }
                                        {newNotes && isFullViewer &&
                                            <div style={{fontSize: "12px"}} className={"m-3"}>
                                                <strong>Notes: </strong>
                                                {newNotes.filter((item) => item.index === index)[0]?.notes || "-"}
                                            </div>
                                        }
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                </StyledMediaContainer>
            </Col>
        </>
    );
};

AuditMedia.propTypes = {
    rowMedia: PropTypes.array,
    mediaHeight: PropTypes.number,
    mediaWidth: PropTypes.number,
    updateElementMedia: PropTypes.func.isRequired,
    elementType: PropTypes.string,
    isViewOnly: PropTypes.bool,
    uploadCountLimit: PropTypes.number,
    mediaType: PropTypes.oneOf([
        "image",
        "video",
    ]),
    deleteMedia: PropTypes.func,
    isFullViewer: PropTypes.bool,
    deleteMarginWidth: PropTypes.string,
    isSmallSizeNoNotes: PropTypes.bool,
    maxVideoDuration: PropTypes.number,
};

export const AuditImages = ({
    isSmallSizeNoNotes,
    rowImages,
    mediaHeight,
    mediaWidth,
    updateElementImages,
    elementType,
    uploadCountLimit,
    deleteMedia,
    isViewOnly = true,
    isFullViewer = true,
}) => {
    return <>
        <span className="fw-bold pb-0">
            Images
        </span>
        <AuditMedia
            isSmallSizeNoNotes={isSmallSizeNoNotes}
            rowMedia={rowImages}
            mediaWidth={mediaWidth}
            mediaHeight={mediaHeight}
            updateElementMedia={updateElementImages}
            elementType={elementType}
            uploadCountLimit={uploadCountLimit}
            isViewOnly={isViewOnly}
            mediaType={"image"}
            isFullViewer={isFullViewer}
            deleteMedia={deleteMedia}
        />
    </>;
};

AuditImages.propTypes = {
    rowImages: PropTypes.array,
    mediaHeight: PropTypes.number,
    mediaWidth: PropTypes.number,
    updateElementImages: PropTypes.func.isRequired,
    deleteMedia: PropTypes.func.isRequired,
    elementType: PropTypes.string,
    isViewOnly: PropTypes.bool,
    isFullViewer: PropTypes.bool,
    uploadCountLimit: PropTypes.number,
    isSmallSizeNoNotes: PropTypes.bool,
};

export const AuditVideos = ({
    isSmallSizeNoNotes,
    rowVideos,
    mediaHeight,
    mediaWidth,
    updateElementVideos,
    elementType,
    uploadCountLimit,
    isViewOnly = true,
    deleteMedia,
    maxVideoDuration = DEFAULT_MAX_VIDEO_DURATION,
    isFullViewer = true,
}) => {
    return <>
        <span className="fw-bold pb-0">
            Videos
        </span>
        <AuditMedia
            isSmallSizeNoNotes={isSmallSizeNoNotes}
            rowMedia={rowVideos}
            mediaWidth={mediaWidth}
            mediaHeight={mediaHeight}
            updateElementMedia={updateElementVideos}
            elementType={elementType}
            uploadCountLimit={uploadCountLimit}
            isViewOnly={isViewOnly}
            mediaType={"video"}
            isFullViewer={isFullViewer}
            deleteMedia={deleteMedia}
            maxVideoDuration={maxVideoDuration}
        />
        {maxVideoDuration < DEFAULT_MAX_VIDEO_DURATION ?
            <SVPLabel
                label={"Maximum video duration"}
                text={`${maxVideoDuration} seconds`}
                icon = {<SVPUpgradeSubscriptionIcon message={"Upgrade to upload longer videos"} />}
            /> :
            <SVPLabel label={"Maximum video duration"} text={`${maxVideoDuration} seconds`} />
        }
    </>;
};

AuditVideos.propTypes = {
    rowVideos: PropTypes.array,
    mediaHeight: PropTypes.number,
    mediaWidth: PropTypes.number,
    updateElementVideos: PropTypes.func.isRequired,
    deleteMedia: PropTypes.func.isRequired,
    elementType: PropTypes.string,
    isViewOnly: PropTypes.bool,
    isFullViewer: PropTypes.bool,
    uploadCountLimit: PropTypes.number,
    isSmallSizeNoNotes: PropTypes.bool,
    maxVideoDuration: PropTypes.number,
};
